import {Injectable, Injector} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpRequest, HttpResponse} from '@angular/common/http';
import {environment} from '@environments/environment';
import {catchError, filter, map} from 'rxjs/operators';
import {HttpResponseInterface} from '@interfaces/http-response.interface';
import {ErrorLoggerService} from '@services/error-logger.service';
import {EventsService} from '@services/events.service';
import {Observable} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';

export interface RequestOptions<T = any> {
    id?: number|string;
    url?: string;
    queryParams?: object|any;
    dataBody?: T;
}

interface ApiServiceInterface {
    get(options?: RequestOptions);
    post(options: RequestOptions);
    put(url: string, body: any, id: string|number);
    delete(options: RequestOptions);
    deleteMultiple(options?: RequestOptions);
}

/**
 * Параметры ответа для запроса с пагинацией
 */
export interface ApiPaginationResponse<T> {
    items: T[];
    page: number;
    pages_total: number;
    total_found_count: number;
    total_items_count: number;
}

@Injectable()
export class ApiService implements ApiServiceInterface {

    constructor(
        protected http: HttpClient,
        protected logger: ErrorLoggerService,
        private injector: Injector,
    ) {

        this.eventsService = this.injector.get(EventsService);
        this._route = this.injector.get(ActivatedRoute);
        this._router = this.injector.get(Router);
    }

    get url(): string {
        return this.baseUrl;
    }

    protected _router: Router;
    protected _route: ActivatedRoute;

    protected baseUrl: string;

    protected eventsService: EventsService;

    /**
     * Временное хранилище url, чтобы достать его, если нужно залогировать ошибку.
     * Примерыы значений
     * GET /api/path,
     * POST /api/post/path
     */
    private urlTempContainer: string;

    public getFormDataUrl(id: number) {
        return `${this.baseUrl}/${id}/form-data`;
    }

    get(options: RequestOptions = {}) {
        return this.request('GET', this.getPathWithId(options.url || this.baseUrl, options.id), null, options.queryParams);
    }

    post<RequestData = any, ResponseData = any>(options: RequestOptions<RequestData> = {}): Observable<ResponseData> {
        return this.request<ResponseData>(
            'POST',
            this.getPathWithId(options.url || this.baseUrl, options.id),
            options.dataBody,
            options.queryParams
        );
    }

    put(body: any, id: string|number, url: string = this.baseUrl) {
        return this.request('PUT', this.getPathWithId(url, id), body);
    }

    delete(options: RequestOptions = {}) {
        return this.request('DELETE', this.getPathWithId(options.url || this.baseUrl, options.id));
    }

    deleteMultiple(options: RequestOptions = {}) {
        return this.request('DELETE', options.url || this.baseUrl, null, options.queryParams);
    }

    private getPathWithId(url: string, id: string|number): string {
        return url + (id ? ('/' + id) : '');
    }

    private request<K = any>(
        method: 'GET' | 'POST' | 'DELETE' | 'PUT' = 'GET',
        url: string,
        body: any = null,
        params: any = null
    ) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        const requestUrl = environment.apiUrl + url;

        const reqParams = params ? this.convertObjectToParams(params) : null;

        const request = new HttpRequest(method, requestUrl, body, {
            headers,
            params: reqParams,
            responseType: 'json',
        });

        this.urlTempContainer = `${method} ${requestUrl}`;

        return this.http.request(request).pipe(
            filter(r => {
                return r instanceof HttpResponse;
            }),
            /**
             * Забираем только Body, так как только оно нас интересует, если статус 200.
             * Если статус будет не 200, то сработает catchError
             */
            map((r: HttpResponse<any>) => {
                return r.body;
            }),
            map((res: HttpResponseInterface) => {
                /**
                 * Либо возвращаем готовые данные, когда status == true,
                 * либо кидаем ошибки из ответа
                 */
                if (res && res.status) {
                    return res.data;
                }

                if (res && !res.status && res.errors) {
                    throw res.errors;
                }

                /**
                 * Либо мы получили вообще не то, что ожидали...
                 */
                throw ['Некорректный формат ответа от сервера'];
            }),
            catchError((e: HttpErrorResponse | any) => {
                if (e instanceof HttpErrorResponse) {
                    this.handleHttpFailErrors(e);

                    /**
                     * Проталкиваем данные как null, чтобы интерфейс не реагировал на такие ошибки, так как это не в его компетенциях
                     */
                    throw null;
                }

                throw e;
            })
        );
    }

    public handleHttpFailErrors(error: HttpErrorResponse) {
        this.eventsService.showGeneralHttpError();
        this.logger.log(error, 'Error handled in ApiService.handleHttpFailErrors with path = ' + this.urlTempContainer);
    }

    private convertObjectToParams(obj: object): HttpParams {
        return Object.keys(obj).reduce((params: HttpParams, key) => {
            /**
             * Исключаем из параметров пустые значения
             */
            if ([undefined, null, ''].includes(obj[key])) {
                return params;
            }
            if (obj[key] instanceof Array) {
                obj[key].forEach(v => {
                    params = params.append(key + '[]', v);
                });
                return params;
            }

            return params.set(key, obj[key]);
        }, new HttpParams());
    }
}

